<script setup>
import {
  ref,
  onMounted,
  onUnmounted,
  computed,
  watch,
  onBeforeUnmount,
} from "vue";
import { useTagStore } from "@/stores/useTagStore";
import { useUserStore } from "@/stores/useUserStore";

import { toTitleCase } from "@/utils/stringUtils";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";

// Componentes modais
import AddTagForm from "./AddTagForm.vue";
import EditTagForm from "./EditTagForm.vue";
import DeleteTagForm from "./DeleteTagForm.vue";
import TagCardModal from "./TagCardModal.vue";
import BatchDeleteTagForm from "./BatchDeleteTagForm.vue";
import SearchTagInput from "./SearchTagInput.vue";
import TagHelpModal from "./TagHelpModal.vue";

// Paginação
import PaginationButtons from "../Generics/PaginationButtons.vue";
import PaginationInfo from "../Generics/PaginationInfo.vue";

// Store
const tagStore = useTagStore();
const userStore = useUserStore();

// Estados de exibição dos modais
const showAddModal = ref(false);
const showEditModal = ref(false);
const showCardModal = ref(false);
const showDeleteModal = ref(false);
const showBatchDeleteModal = ref(false);
const showHelpModal = ref(false);

const selectedTagId = ref(null);

// Paginação
const currentPage = ref(1);
const pageSize = ref(10);

// [NOVO] controle de carregamento da tabela
const isLoading = ref(false);

// Lógica de Dropdowns e Seleção
const openDropdownId = ref(null);
const isBatchDropdownOpen = ref(false);
const batchDropdownRef = ref(null);

// ====================================================================
// ✅ LÓGICA DO TOAST INTEGRADA
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

function resetToastTimeout(duration) {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, duration);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout(6000);
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout(6000);
}

function handleChildNotify(payload) {
  if (!payload) return;

  if (payload.type === "success") {
    showSuccessToast(payload.message || "Operação realizada com sucesso.");
    return;
  }

  showErrorToast(payload.message || "Ocorreu um erro.");
}

async function handleTagDeleted() {
  showDeleteModal.value = false;
  tagStore.selectedTagIds = [];
  await fetchTagsPaged();
}

async function handleBatchTagsDeleted() {
  showBatchDeleteModal.value = false;
  tagStore.selectedTagIds = [];
  await fetchTagsPaged();
}

// ====================================================================

// ====================================================================
// ✅ REGRA SYS / VISUAL
// ====================================================================
function normalizeUserName(name) {
  return (name ?? "").toString().trim().toLowerCase();
}
function isLoggedSys() {
  return normalizeUserName(userStore.currentUser?.userName) === "sys";
}
function isCreatedBySys(subtype) {
  return normalizeUserName(subtype?.createdByUserName) === "sys";
}
function isRowSelectable(subtype) {
  const logged = normalizeUserName(userStore.currentUser?.userName);
  if (!logged) return false;

  if (logged === "sys") return true;

  const creator = normalizeUserName(subtype?.createdByUserName);
  if (!creator) return true;

  return creator !== "sys";
}
function sysProtectedTooltip(subtype) {
  if (!subtype) return "";
  if (isLoggedSys()) return "";
  if (!isCreatedBySys(subtype)) return "";
  return "Protegido (criado pelo Sys)";
}
// ====================================================================

// =========================================================================
// 💡 LÓGICA DE ESTILOS PARA PREVIEW NA TABELA
// =========================================================================

function getSingleWordPreviewStyle(tag) {
  return {
    color: tag.specificColor,
    fontWeight: "600",
    fontSize: "0.8rem",
  };
}

function getMultiWordContainerStyle() {
  return {};
}

function getMultiWordPreviewStyle(tag) {
  return {
    display: "inline-block",
    backgroundColor: tag.specificBackground,
    borderRadius: "0.5rem",
    padding: "1px 3px",
    fontSize: "0.8rem",
    lineHeight: "1.1",
  };
}

// =========================================================================
/* LÓGICA DE SELEÇÃO E AÇÕES */
// =========================================================================

const isAllSelectedOnPage = computed({
  get: () => {
    const selectableOnPage = (tagStore.tags ?? []).filter((ns) =>
      isRowSelectable(ns)
    );
    if (selectableOnPage.length === 0) return false;

    return selectableOnPage.every((ns) =>
      tagStore.selectedTagIds.includes(ns.tagId)
    );
  },
  set: (value) => {
    const idsOnPage = (tagStore.tags ?? []).map((ns) => ns.tagId);

    const selectableIdsOnPage = (tagStore.tags ?? [])
      .filter((ns) => isRowSelectable(ns))
      .map((ns) => ns.tagId);

    if (value) {
      selectableIdsOnPage.forEach((id) => {
        if (!tagStore.selectedTagIds.includes(id)) {
          tagStore.selectedTagIds.push(id);
        }
      });
    } else {
      tagStore.selectedTagIds = tagStore.selectedTagIds.filter(
        (id) => !idsOnPage.includes(id)
      );
    }
  },
});

const anySelectableOnPage = computed(() =>
  (tagStore.tags ?? []).some((t) => isRowSelectable(t))
);

const canBatchActions = computed(() => {
  return (
    Array.isArray(tagStore.selectedTagIds) && tagStore.selectedTagIds.length > 0
  );
});

function toggleRowDropdown(tagId) {
  openDropdownId.value = openDropdownId.value === tagId ? null : tagId;
}

function handleEdit(index) {
  indexSelector(index);
  showEditModal.value = true;
  openDropdownId.value = null;
}

function handleOpenCard(index) {
  const tag = tagStore.tags[index];
  selectedTagId.value = tag.tagId;
  tagStore.setIdTag(tag.tagId);
  showCardModal.value = true;
}

function handleView(index) {
  handleOpenCard(index);
  openDropdownId.value = null;
}

function handleDelete(index) {
  indexSelector(index);
  showDeleteModal.value = true;
  openDropdownId.value = null;
}

function handleBatchDelete() {
  isBatchDropdownOpen.value = false;

  const selectedIds = tagStore.selectedTagIds || [];
  if (selectedIds.length === 0) {
    showErrorToast("Nenhuma tag selecionada.");
    return;
  }

  showBatchDeleteModal.value = true;
}

function toggleSelection(tag) {
  if (!isRowSelectable(tag)) return;
  tagStore.toggleSelection(tag.tagId);
}

async function fetchTagsPaged() {
  isLoading.value = true;

  try {
    await tagStore.fetchTagsPaged(
      currentPage.value,
      pageSize.value,
      tagStore.searchTerm
    );

    if (!isLoggedSys() && Array.isArray(tagStore.tags)) {
      const sysIdsOnPage = tagStore.tags
        .filter((ns) => isCreatedBySys(ns))
        .map((ns) => ns.tagId);

      if (sysIdsOnPage.length) {
        tagStore.selectedTagIds = tagStore.selectedTagIds.filter(
          (id) => !sysIdsOnPage.includes(id)
        );
      }
    }

    if (tagStore.shouldForcePageOne) {
      tagStore.shouldForcePageOne = false;

      if (currentPage.value !== 1) {
        currentPage.value = 1;
      } else {
        await tagStore.fetchTagsPaged(1, pageSize.value, tagStore.searchTerm);

        if (!isLoggedSys() && Array.isArray(tagStore.tags)) {
          const sysIdsOnPage = tagStore.tags
            .filter((ns) => isCreatedBySys(ns))
            .map((ns) => ns.tagId);

          if (sysIdsOnPage.length) {
            tagStore.selectedTagIds = tagStore.selectedTagIds.filter(
              (id) => !sysIdsOnPage.includes(id)
            );
          }
        }
      }
    }
  } catch (error) {
    console.error(
      "❌ Erro ao buscar subtipos de nota paginados:",
      error?.message
    );
    showErrorToast("Erro ao carregar tags de nota.");
  } finally {
    isLoading.value = false;
  }
}

watch(currentPage, fetchTagsPaged);

function handleClickOutside(event) {
  if (
    batchDropdownRef.value &&
    !batchDropdownRef.value.contains(event.target)
  ) {
    isBatchDropdownOpen.value = false;
  }
  if (openDropdownId.value && !event.target.closest(".action-cell-container")) {
    openDropdownId.value = null;
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  fetchTagsPaged();
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);

  tagStore.searchTerm = "";
  tagStore.currentPage = 1;
  tagStore.shouldForcePageOne = true;

  tagStore.selectedTagIds = [];
  openDropdownId.value = null;
  isBatchDropdownOpen.value = false;
  selectedTagId.value = null;
});

function indexSelector(index) {
  const tag = tagStore.tags[index];
  tagStore.setIdTag(tag.tagId);
}

async function handleTagAdded() {
  if (currentPage.value !== 1) currentPage.value = 1;
  await fetchTagsPaged();
}

function canManage(subtype) {
  const logged = (userStore.currentUser?.userName ?? "").trim().toLowerCase();
  if (!logged) return false;

  if (logged === "sys") return true;

  const creator = (subtype?.createdByUserName ?? "").trim().toLowerCase();

  if (!creator) return true;

  return creator !== "sys";
}
</script>

<template>
  <div class="w-full h-full">
    <div class="main-container">
      <div class="main-header">
        <div class="flex flex-col gap-0">
          <div class="text-xl font-bold">Tags de Nota</div>
          <div class="text-sm mt-1 text-gray-700 ">
            Esta seção permite gerenciar as tags apontadas nas notas das
            atividades e seus destaques visuais.
            <button
              type="button"
              class="underline text-blue-600 hover:text-blue-800 ml-1"
              @click.stop="showHelpModal = true"
            >
              Saiba mais
            </button>
          </div>
        </div>
      </div>

      <div class="action-bar">
        <div class="w-full md:w-1/2">
          <SearchTagInput @search="fetchTagsPaged" />
        </div>
        <div
          class="w-full md:w-auto flex flex-col md:flex-row space-y-2 md:space-y-0 items-stretch md:items-center justify-end md:space-x-3 flex-shrink-0"
        >
          <button @click="showAddModal = true" class="btn btn-primary">
            <svg class="h-4 w-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path
                clip-rule="evenodd"
                fill-rule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              />
            </svg>
            Adicionar tag
          </button>

          <div ref="batchDropdownRef" class="relative">
            <button
              @click="isBatchDropdownOpen = !isBatchDropdownOpen"
              class="btn btn-batch w-full"
              :disabled="!canBatchActions"
            >
              Ações em lote
              <svg
                class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                :class="{ open: isBatchDropdownOpen }"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  clip-rule="evenodd"
                  fill-rule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                />
              </svg>
            </button>

            <div v-if="isBatchDropdownOpen" class="dropdown-menu">
              <div class="py-1">
                <a
                  href="#"
                  role="button"
                  @click.prevent="handleBatchDelete"
                  class="dropdown-item danger-item"
                >
                  <i class="fas fa-trash-alt"></i>
                  <span>Apagar selecionados</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="table-wrapper">
        <table class="main-table">
          <thead>
            <tr>
              <th style="width: 4%">
                <input
                  id="checkbox-all"
                  type="checkbox"
                  v-model="isAllSelectedOnPage"
                  class="checkbox-style"
                  :disabled="!anySelectableOnPage"
                  :class="{ 'disabled-item': !anySelectableOnPage }"
                />
              </th>
              <th style="width: 5%">Ações</th>
              <th style="width: 5%">Id</th>
              <th style="width: 30%">Tag de Nota</th>
              <th style="width: 15%">Nota única palavra</th>
              <th style="width: 15%">Nota trecho</th>
              <th style="width: 13%">Criador</th>
              <th style="width: 13%">Data Atualização</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(tag, index) in !isLoading ? tagStore.tags : []"
              :key="tag.tagId"
              @click="handleOpenCard(index)"
              class="clickable-row"
            >
              <td
                @click.stop
                :class="{
 'checkbox-cell-disabled': !isRowSelectable(tag),
                }"
              >
                <input
                  type="checkbox"
                  :value="tag.tagId"
                  :checked="tagStore.selectedTagIds.includes(tag.tagId)"
                  :disabled="!isRowSelectable(tag)"
                  :title="sysProtectedTooltip(tag)"
                  :class="[
 'checkbox-style',
                    { 'checkbox-disabled': !isRowSelectable(tag) },
                  ]"
                  @change="toggleSelection(tag)"
                />
              </td>

              <td @click.stop class="action-cell-container">
                <button
                  @click="toggleRowDropdown(tag.tagId)"
                  class="btn-icon btn-soft-brand"
                  title="Menu de Ações"
                >
                  <i class="fas fa-ellipsis-h fa-lg"></i>
                </button>

                <div
                  v-if="openDropdownId === tag.tagId"
                  class="row-dropdown-menu"
                >
                  <ul>
                    <li
                      :class="{ 'disabled-item': !canManage(tag) }"
                      @click="canManage(tag) && handleEdit(index)"
                    >
                      <i class="fas fa-pencil-alt"></i><span>Editar</span>
                    </li>

                    <li @click="handleView(index)">
                      <i class="fas fa-eye"></i><span>Visualizar</span>
                    </li>

                    <li
                      class="danger-item"
                      :class="{ 'disabled-item': !canManage(tag) }"
                      @click="canManage(tag) && handleDelete(index)"
                    >
                      <i class="fas fa-trash-alt"></i><span>Apagar</span>
                    </li>
                  </ul>
                </div>
              </td>

              <td>
                <span :title="tag.tagId" style="font-size: smaller">
                  {{ tag.tagId }}
                </span>
              </td>

              <td class="cell-text-left">
                <span class="truncate" :title="toTitleCase(tag.tagName)">
                  {{ toTitleCase(tag.tagName) }}
                </span>
              </td>

              <td class="cell-preview-container">
                <div
                  :style="getSingleWordPreviewStyle(tag)"
                  :title="
                    'Cor do texto: ' +
                    tag.specificColor +
                    ' | Fundo: ' +
                    tag.specificBackground
                  "
                >
                  Palavra
                </div>
              </td>

              <td class="cell-preview-container">
                <div :style="getMultiWordContainerStyle()">
                  <span
                    :style="getMultiWordPreviewStyle(tag)"
                    :title="
                      'Sublinhado: ' +
                      (tag.requiredUnderline ? tag.specificUnderline : 'Não')
                    "
                  >
                    Várias palavras
                  </span>
                </div>
              </td>

              <td class="cell-creator">
                <div
                  class="creator-cell"
                  :class="{ 'creator-center': isCreatedBySys(tag) }"
                  :title="toTitleCase(tag.createdByUserName)"
                >
                  <template v-if="isCreatedBySys(tag)">
                    <span class="sys-badge" title="Criado pelo Sys">SYS</span>
                  </template>

                  <template v-else>
                    <span class="truncate">
                      {{ toTitleCase(tag.createdByUserName) }}
                    </span>
                  </template>
                </div>
              </td>

              <td>
                <span
                  :title="formatDateTimeForDisplay(tag.updatedAt)"
                  style="font-size: smaller"
                >
                  {{ formatDateTimeForDisplay(tag.updatedAt) }}
                </span>
              </td>
            </tr>

            <tr v-if="isLoading" class="empty-row-state">
              <td colspan="8" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <svg class="empty-spinner" viewBox="0 0 24 24" fill="none">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  <p>Carregando tags...</p>
                </div>
              </td>
            </tr>

            <tr v-else-if="tagStore.tags.length === 0" class="empty-row-state">
              <td colspan="8" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <img src="/img/lupa.webp" alt="" width="38" height="40" class="empty-icon-img" />
                  <p>Nenhuma tag encontrada.</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="!isLoading && (tagStore.totalCount ?? 0) > 0"
          class="tag-pagination-row"
        >
          <div class="tag-pagination-left">
            <span class="tag-pagination-label">Mostrando</span>
            <PaginationInfo
              :current-page="currentPage"
              :page-size="pageSize"
              :total-count="tagStore.totalCount ?? 0"
            />
            <span>registros</span>
          </div>

          <PaginationButtons
            :current-page="currentPage"
            :page-size="pageSize"
            :total-count="tagStore.totalCount ?? 0"
            :total-pages="tagStore.totalPages ?? 0"
            @change-page="(page) => (currentPage = page)"
          />
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <AddTagForm
      v-if="showAddModal"
      @close="showAddModal = false"
      @tag-added="handleTagAdded"
    />
    <EditTagForm
      v-if="showEditModal"
      @close="showEditModal = false"
      @refresh="fetchTagsPaged"
    />
    <TagCardModal
      v-if="showCardModal"
      :show="showCardModal"
      :tagId="selectedTagId"
      @close="showCardModal = false"
      @tag-updated="fetchTagsPaged"
      @notify="handleChildNotify"
    />

    <DeleteTagForm
      v-if="showDeleteModal"
      @close="showDeleteModal = false"
      @refresh="handleTagDeleted"
      @notify="handleChildNotify"
    />
    <BatchDeleteTagForm
      v-if="showBatchDeleteModal"
      @close="showBatchDeleteModal = false"
      @refresh="handleBatchTagsDeleted"
      @notify="handleChildNotify"
    />
  </Teleport>

  <Teleport to="body">
    <TagHelpModal :show="showHelpModal" @close="showHelpModal = false" />
  </Teleport>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
@media (min-width: 768px) {
  .action-bar {
    flex-direction: row;
    align-items: center;
  }
}

.row-dropdown-menu li.dropdown-item-disabled:hover {
  transform: none;
  box-shadow: none;
}

.main-table .cell-preview-container {
  padding: 0.25rem 0.2rem;
  display: table-cell;
  vertical-align: middle;
}

.checkbox-cell-disabled * {
  cursor: not-allowed !important;
}

.sys-badge {
  margin-right: 6px;
}

.creator-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}
.creator-cell .truncate {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.creator-cell .sys-badge {
  margin-left: 0;
}
.creator-center {
  justify-content: center;
}

.toast-title {
  margin-bottom: 0.2rem;
}

.cell-creator {
  text-align: center;
}
.cell-creator > .creator-cell {
  display: flex !important;
  align-items: center;
  justify-content: center !important;
  width: 100% !important;
  max-width: none !important;
  margin: 0 !important;
}
.cell-creator > .creator-cell .truncate {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.main-table td > div.creator-cell {
  display: flex !important;
  width: 100% !important;
  max-width: none !important;
  margin: 0 !important;
}
.main-table td > div.creator-cell.creator-center {
  justify-content: center !important;
}

.tag-pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem 1rem;
  font-size: 0.82rem;
}
.tag-pagination-left {
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.tag-pagination-label {
  padding-top: 2px;
}

.empty-state-content {
  width: 100%;
  margin: 0 auto;
}

</style>